/**
 * Dots of Care — global feed of every traveller touchpoint the team has
 * delivered, with type filters and quick logging.
 */
import React, { useCallback, useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import { CARE_TYPES, CareTimeline, CareMomentDialog } from '@/components/CareMoments';
import { HandHeart, Plus, Loader2 } from 'lucide-react';

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function PollenDotsCare() {
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/pollen/care-moments`, { credentials: 'include', headers: authHeaders() });
      if (r.ok) setMoments(await r.json());
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    try {
      await fetch(`${API}/pollen/care-moments/${deleteTarget.id}`, { method: 'DELETE', credentials: 'include', headers: authHeaders() });
      setMoments(prev => prev.filter(x => x.id !== deleteTarget.id));
      toast.success('Care moment removed');
    } catch (e) { toast.error('Failed to remove'); }
    setDeleteTarget(null);
  };

  const counts = moments.reduce((acc, m) => { acc[m.care_type] = (acc[m.care_type] || 0) + 1; return acc; }, {});
  const filtered = typeFilter ? moments.filter(m => m.care_type === typeFilter) : moments;

  return (
    <Layout>
      <div className="max-w-3xl space-y-4" data-testid="dots-of-care-page">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <HandHeart className="w-6 h-6 text-rose-600" />Dots of Care
            </h1>
            <p className="text-sm text-muted-foreground">Every small act of care we've delivered — {moments.length} and counting.</p>
          </div>
          <Button onClick={() => setAddOpen(true)} data-testid="care-page-add-btn">
            <Plus className="w-4 h-4 mr-1" />Log Care Moment
          </Button>
        </div>

        <div className="flex flex-wrap gap-1.5" data-testid="care-type-filters">
          <button onClick={() => setTypeFilter('')}
            className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${!typeFilter ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-muted-foreground hover:bg-muted'}`}
            data-testid="care-filter-all">
            All ({moments.length})
          </button>
          {Object.entries(CARE_TYPES).map(([key, cfg]) => {
            if (!counts[key]) return null;
            const Icon = cfg.icon;
            const active = typeFilter === key;
            return (
              <button key={key} onClick={() => setTypeFilter(active ? '' : key)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${active ? cfg.chip + ' ring-1 ring-current' : 'bg-white text-muted-foreground hover:bg-muted'}`}
                data-testid={`care-filter-${key}`}>
                <Icon className="w-3 h-3" />{cfg.label} ({counts[key]})
              </button>
            );
          })}
        </div>

        <Card>
          <CardContent className="pt-5">
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10">
                <HandHeart className="w-10 h-10 text-rose-200 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No care moments yet.</p>
                <p className="text-xs text-muted-foreground mt-1">Log the first birthday cake, welcome kit or special surprise your team delivered.</p>
              </div>
            ) : (
              <CareTimeline moments={filtered} onDelete={setDeleteTarget} showCustomer showTour />
            )}
          </CardContent>
        </Card>

        <CareMomentDialog open={addOpen} onOpenChange={setAddOpen} onSaved={(m) => setMoments(prev => [m, ...prev])} />
        <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
          title="Remove this care moment?" description={deleteTarget?.title || ''} onConfirm={confirmDelete} />
      </div>
    </Layout>
  );
}
